import { Component, inject, input, ChangeDetectionStrategy, computed, effect, untracked } from '@angular/core';
import {
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { ControlValueAccessor, FormBuilder } from '@angular/forms';
import { uniqueId } from '@co-3/ctrl-ui/common';
import { startWith } from 'rxjs';
import { CtrlAutocompleteComponent, CtrlAutocompleteFilterFn } from '@co-3/ctrl-ui/forms';
import { FormLabelPipe } from 'app/shared/pipes/form-label.pipe';
import { ReactiveLabelDirective } from 'app/shared/directives/reactive-label.directive';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * @description Factory function that returns a {@link ValidatorFn} to validate
 * the internal form of the {@link AutocompleteWithFallbackInputComponent}.
 *
 * The internal form is valid when
 * 1. the value of autocomplete is in the list of options, or
 * 2. the value of autocomplete is the fallback options and
 *      - minLength is either -1 or the length of the fallback value is higher or equal to minLength, and
 *      - maxLength is either -1 or the length of the fallback value is lower or equal to maxLength.
 * In all other cases the internal form is invalid, and appropriate errors will be set on both
 * `FormControl`s.
 * @param {number} minLength The minimum length of the fallback value. -1 means no min length.
 * @param {number} maxLength The maximum length of the fallback value. -1 means no max length.
 * @param {string[]} options The options available to the user. the value of autocomplete should
 * be included in this if it is not the {@link fallbackOption}
 * @param {string} fallbackOption This is the option the user can select when they don't want
 * any of the other options.
 * @returns {ValidatorFn} A validator function checking the conditions as described above.
 */
const validateAutocomplete =
    (minLength: number, maxLength: number, options: string[], fallbackOption: string): ValidatorFn =>
    (form: FormGroup<{ autocomplete: FormControl<string>; fallback: FormControl<string> }>) => {
        const autocomplete = form.controls.autocomplete.value;
        const fallback = form.controls.fallback.value;
        const errors: ValidationErrors = {};

        if (autocomplete == null && fallback == null) {
            errors['required'] = true;
        }
        if (autocomplete === fallbackOption) {
            if (minLength !== -1 && fallback.length < minLength) {
                errors['minLength'] = true;
            }
            if (maxLength !== -1 && fallback.length > maxLength) {
                errors['maxLength'] = true;
            }
        } else if (!options.includes(autocomplete)) {
            errors['invalidSelection'] = true;
        }

        if (Object.keys(errors).length) {
            form.controls.autocomplete.setErrors(errors);
            form.controls.fallback.setErrors(errors);
            return errors;
        }
        form.controls.autocomplete.setErrors(null);
        form.controls.fallback.setErrors(null);
        return null;
    };

/**
 * @description A component that implements the editable combobox with list autocomplete pattern,
 * but allows selecting a fallback option when none of the options on the list are applicable.
 * The user can then enter their wanted option in an additional input. This pattern should
 * encourage users to pick one of the options from the list, and only enter another value if
 * they don't find it there.
 *
 * This component has an internal form with it's own validator (see {@link validateAutocomplete})
 * \- keep that in mind when validating the bound form control or you might get differing
 * validation states on the internal and external form, which might confuse users.
 */
@Component({
    selector: 'app-autocomplete-with-fallback-input',
    imports: [ReactiveFormsModule, CtrlAutocompleteComponent, FormLabelPipe, ReactiveLabelDirective],
    templateUrl: './autocomplete-with-fallback-input.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: AutocompleteWithFallbackInputComponent, multi: true }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteWithFallbackInputComponent implements ControlValueAccessor {
    public options = input<string[]>([]);
    public optionsLabel = input<string>('Bitte wählen');

    public fallbackOption = input<string>('Sonstige');
    public fallbackOptionLabel = input<string>('Bitte wählen');

    public placeholder = input<string>('Bitte wählen');
    public requiredErrorLabel = input<string>('Feld erforderlich');
    public minLength = input<number>(2);
    public maxLength = input<number>(-1);

    protected id = uniqueId();
    protected fallbackErrorId = uniqueId('fallback-error-');

    protected form = inject(FormBuilder).group({
        autocomplete: [null as string | null],
        fallback: [''],
    });

    protected filterFn: CtrlAutocompleteFilterFn<string> = (opts: string[], query: string) => {
        const normalizedQuery = query.trim().toLocaleLowerCase();
        return [
            ...opts.filter((option: string) => option.toLocaleLowerCase().includes(normalizedQuery)),
            this.fallbackOption(),
        ];
    };

    private formValue = toSignal(this.form.valueChanges.pipe(startWith(this.form.value)));

    protected fallbackUsedFlag = computed(() => `${this.fallbackOption()}: `);

    protected showFallbackInput = computed(() => {
        const shouldShow = this.formValue().autocomplete === this.fallbackOption();
        if (!shouldShow) {
            this.form.controls.fallback.markAsUntouched();
        }
        return shouldShow;
    });

    protected showFallbackInputError = computed(() => {
        this.formValue();
        return this.form.controls.fallback.touched && this.form.controls.fallback.hasError('required');
    });

    public constructor() {
        // focus fallback input when it becomes visible
        effect(() => {
            if (this.showFallbackInput()) {
                const fallbackInputId = `fallback-input-${this.id}`;
                // wait for the input to be in the dom before focussing on it
                setTimeout(() => {
                    const el = document.getElementById(fallbackInputId) as HTMLElement;
                    el?.focus();
                }, 1);
            }
        });
        // call onChange when the form value updates
        effect(() => {
            const val = this.formValue();

            untracked(() => {
                if (val.autocomplete === this.fallbackOption()) {
                    this.onChange?.(`${this.fallbackUsedFlag()}${val.fallback}`);
                } else {
                    if (this.options().includes(val.autocomplete)) {
                        this.onChange?.(val.autocomplete);
                    }
                }
            });
        });
        // reset autocomplete validator when options change
        effect(() => {
            this.form.setValidators(
                validateAutocomplete(this.minLength(), this.maxLength(), this.options(), this.fallbackOption()),
            );
            this.form.updateValueAndValidity();
        });
    }

    private stripFallbackUsedFlag(val: string): string {
        const flag = this.fallbackUsedFlag();
        return val.replace(flag, '');
    }

    // #region ControlValueAccessor interface

    private onChange: (val: string) => void;
    protected onTouch: () => void;

    public writeValue(value: string | null): void {
        if (typeof value === 'string' && value !== '') {
            if (this.options().includes(value)) {
                this.form.patchValue({ autocomplete: value, fallback: '' });
            } else {
                if (value === this.fallbackOption() || value === this.fallbackUsedFlag()) {
                    this.form.patchValue({ autocomplete: this.fallbackOption(), fallback: '' });
                } else {
                    this.form.patchValue({
                        autocomplete: this.fallbackOption(),
                        fallback: this.stripFallbackUsedFlag(value),
                    });
                }
            }
        } else {
            this.form.patchValue({ autocomplete: null, fallback: '' });
        }
    }

    public registerOnChange(fn: (val: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    // #endregion
}
